/**
 * Chatbot Store
 * 
 * Zustand store for managing chatbot state, including:
 * - Chatbot list and selection
 * - Dashboard statistics
 * - Loading states and error handling
 * - CRUD operations with optimistic updates
 */

import React from 'react'
import { create } from 'zustand'
import type { Chatbot, CreateChatbotRequest, ChatbotAnalytics } from '../types'
import { chatbotsService } from '../services/chatbots'
import { logger } from '../utils/logger'

/**
 * Chatbot Store State Interface
 */
interface ChatbotState {
  // Data state
  chatbots: Chatbot[]
  selectedChatbot: Chatbot | null
  totalChatbots: number
  currentPage: number
  pageSize: number
  
  // Dashboard statistics
  dashboardStats: {
    totalChatbots: number
    totalMessages: number
    avgResponseTime: number
    userSatisfaction: number
  }
  
  // Loading states
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  isLoadingStats: boolean
  
  // Error handling
  error: string | null
  lastFetch: Date | null
}

/**
 * Chatbot Store Actions Interface
 */
interface ChatbotActions {
  // Data fetching
  fetchChatbots: (page?: number, limit?: number, forceRefresh?: boolean) => Promise<void>
  fetchChatbot: (id: string) => Promise<void>
  fetchDashboardStats: () => Promise<void>
  
  // CRUD operations
  createChatbot: (data: CreateChatbotRequest) => Promise<Chatbot>
  updateChatbot: (id: string, data: Partial<CreateChatbotRequest>) => Promise<void>
  deleteChatbot: (id: string) => Promise<void>
  
  // Selection and navigation
  selectChatbot: (chatbot: Chatbot | null) => void
  
  // State management
  clearError: () => void
  resetStore: () => void
  setPage: (page: number) => void
}

/**
 * Combined Store Type
 */
type ChatbotStore = ChatbotState & ChatbotActions

/**
 * Initial State
 */
const initialState: ChatbotState = {
  // Data state
  chatbots: [],
  selectedChatbot: null,
  totalChatbots: 0,
  currentPage: 1,
  pageSize: 20,
  
  // Dashboard statistics
  dashboardStats: {
    totalChatbots: 0,
    totalMessages: 0,
    avgResponseTime: 0,
    userSatisfaction: 0
  },
  
  // Loading states
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isLoadingStats: false,
  
  // Error handling
  error: null,
  lastFetch: null
}

/**
 * Chatbot Store Implementation
 */
export const useChatbotStore = create<ChatbotStore>((set, get) => ({
  ...initialState,

  /**
   * Fetch chatbots with pagination and caching
   */
  fetchChatbots: async (page = 1, limit = 20, forceRefresh = false) => {
    const state = get()
    
    // Skip if already loading or recently fetched (unless force refresh)
    if (state.isLoading) {
      logger.debug('Skipping fetch - already loading', { component: 'ChatbotStore' })
      return
    }
    
    
    if (!forceRefresh && state.lastFetch && state.currentPage === page) {
      const timeSinceLastFetch = Date.now() - state.lastFetch.getTime()
      if (timeSinceLastFetch < 30000) { // 30 seconds cache
        logger.debug('Skipping fetch - recently fetched', {
          component: 'ChatbotStore',
          data: { timeSinceLastFetch }
        })
        return
      }
    }
    
    try {
      logger.store('ChatbotStore', 'fetch_chatbots_start', { page, limit })
      set({ isLoading: true, error: null })
      
      const response = await chatbotsService.getChatbots(page, limit)
      
      
      logger.store('ChatbotStore', 'fetch_chatbots_success', {
        total: response.total,
        count: response.items.length,
        page: response.page
      })
      
      set({
        chatbots: response.items,
        totalChatbots: response.total,
        currentPage: response.page,
        pageSize: response.size,
        isLoading: false,
        error: null,
        lastFetch: new Date()
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chatbots'
      
      logger.store('ChatbotStore', 'fetch_chatbots_error', { error: errorMessage })
      
      set({
        isLoading: false,
        error: errorMessage
      })
      
      throw error
    }
  },

  /**
   * Fetch a single chatbot by ID
   */
  fetchChatbot: async (id: string) => {
    try {
      logger.store('ChatbotStore', 'fetch_chatbot_start', { id })
      set({ error: null })
      
      const chatbot = await chatbotsService.getChatbot(id)
      
      logger.store('ChatbotStore', 'fetch_chatbot_success', { id, name: chatbot.name })
      
      // Update the chatbot in the list if it exists
      const state = get()
      const updatedChatbots = state.chatbots.map(cb => 
        cb.id === id ? chatbot : cb
      )
      
      set({
        chatbots: updatedChatbots,
        selectedChatbot: chatbot,
        error: null
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch chatbot'
      
      logger.store('ChatbotStore', 'fetch_chatbot_error', { id, error: errorMessage })
      
      set({ error: errorMessage })
      throw error
    }
  },

  /**
   * Fetch dashboard statistics
   */
  fetchDashboardStats: async () => {
    try {
      logger.store('ChatbotStore', 'fetch_dashboard_stats_start')
      set({ isLoadingStats: true, error: null })
      
      const stats = await chatbotsService.getDashboardStats()
      
      logger.store('ChatbotStore', 'fetch_dashboard_stats_success', stats)
      
      set({
        dashboardStats: stats,
        isLoadingStats: false,
        error: null
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard stats'
      
      logger.store('ChatbotStore', 'fetch_dashboard_stats_error', { error: errorMessage })
      
      set({
        isLoadingStats: false,
        error: errorMessage
      })
    }
  },

  /**
   * Create a new chatbot with optimistic update
   */
  createChatbot: async (data: CreateChatbotRequest) => {
    try {
      logger.store('ChatbotStore', 'create_chatbot_start', { name: data.name })
      set({ isCreating: true, error: null })
      
      const newChatbot = await chatbotsService.createChatbot(data)
      
      logger.store('ChatbotStore', 'create_chatbot_success', { 
        id: newChatbot.id, 
        name: newChatbot.name 
      })
      
      // Add to the beginning of the list
      const state = get()
      set({
        chatbots: [newChatbot, ...state.chatbots],
        totalChatbots: state.totalChatbots + 1,
        selectedChatbot: newChatbot,
        isCreating: false,
        error: null
      })
      
      // Refresh dashboard stats
      get().fetchDashboardStats()
      
      return newChatbot
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create chatbot'
      
      logger.store('ChatbotStore', 'create_chatbot_error', { error: errorMessage })
      
      set({
        isCreating: false,
        error: errorMessage
      })
      
      throw error
    }
  },

  /**
   * Update an existing chatbot
   */
  updateChatbot: async (id: string, data: Partial<CreateChatbotRequest>) => {
    try {
      logger.store('ChatbotStore', 'update_chatbot_start', { id, updates: Object.keys(data) })
      set({ isUpdating: true, error: null })
      
      const updatedChatbot = await chatbotsService.updateChatbot(id, data)
      
      logger.store('ChatbotStore', 'update_chatbot_success', { 
        id, 
        name: updatedChatbot.name 
      })
      
      // Update in the list
      const state = get()
      const updatedChatbots = state.chatbots.map(chatbot =>
        chatbot.id === id ? updatedChatbot : chatbot
      )
      
      set({
        chatbots: updatedChatbots,
        selectedChatbot: state.selectedChatbot?.id === id ? updatedChatbot : state.selectedChatbot,
        isUpdating: false,
        error: null
      })
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update chatbot'
      
      logger.store('ChatbotStore', 'update_chatbot_error', { id, error: errorMessage })
      
      set({
        isUpdating: false,
        error: errorMessage
      })
      
      throw error
    }
  },

  /**
   * Delete a chatbot with optimistic update
   */
  deleteChatbot: async (id: string) => {
    try {
      logger.store('ChatbotStore', 'delete_chatbot_start', { id })
      set({ isDeleting: true, error: null })
      
      // Optimistic update - remove from list immediately
      const state = get()
      const chatbotToDelete = state.chatbots.find(cb => cb.id === id)
      const updatedChatbots = state.chatbots.filter(chatbot => chatbot.id !== id)
      
      set({
        chatbots: updatedChatbots,
        totalChatbots: state.totalChatbots - 1,
        selectedChatbot: state.selectedChatbot?.id === id ? null : state.selectedChatbot
      })
      
      await chatbotsService.deleteChatbot(id)
      
      logger.store('ChatbotStore', 'delete_chatbot_success', { 
        id, 
        name: chatbotToDelete?.name 
      })
      
      set({
        isDeleting: false,
        error: null
      })
      
      // Refresh dashboard stats
      get().fetchDashboardStats()
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete chatbot'
      
      logger.store('ChatbotStore', 'delete_chatbot_error', { id, error: errorMessage })
      
      // Revert optimistic update on error
      get().fetchChatbots(get().currentPage, get().pageSize, true)
      
      set({
        isDeleting: false,
        error: errorMessage
      })
      
      throw error
    }
  },

  /**
   * Select a chatbot
   */
  selectChatbot: (chatbot: Chatbot | null) => {
    logger.store('ChatbotStore', 'select_chatbot', { 
      id: chatbot?.id, 
      name: chatbot?.name 
    })
    
    set({ selectedChatbot: chatbot })
  },

  /**
   * Clear error state
   */
  clearError: () => {
    logger.store('ChatbotStore', 'clear_error')
    set({ error: null })
  },

  /**
   * Reset store to initial state
   */
  resetStore: () => {
    logger.store('ChatbotStore', 'reset_store')
    set(initialState)
  },

  /**
   * Set current page for pagination
   */
  setPage: (page: number) => {
    logger.store('ChatbotStore', 'set_page', { page })
    set({ currentPage: page })
    
    // Fetch new page data
    get().fetchChatbots(page, get().pageSize)
  }
}))

/**
 * Hook for chatbot list with automatic fetching
 */
export const useChatbots = () => {
  const store = useChatbotStore()
  
  React.useEffect(() => {
    // Auto-fetch chatbots when component mounts (only once)
    if (store.chatbots.length === 0 && !store.isLoading) {
      logger.debug('Auto-fetching chatbots on mount', {
        component: 'useChatbots',
        action: 'auto_fetch'
      })
      store.fetchChatbots()
    }
    
    // Auto-fetch dashboard stats (only once)
    if (store.dashboardStats.totalChatbots === 0 && !store.isLoadingStats) {
      logger.debug('Auto-fetching dashboard stats on mount', {
        component: 'useChatbots',
        action: 'auto_fetch_stats'
      })
      store.fetchDashboardStats()
    }
  }, []) // Empty dependency array - only run on mount
  
  return store
}

